import './metaAttribution'

const queueKey = 'meta.events.v1'
const sentKey = 'meta.sent.v1'
const maxAge = 24 * 60 * 60 * 1000
let pixelId = null
let sdkPromise = null
const initialized = {}
let waiters = []

function read (key, fallback) {
  try { return JSON.parse(window.sessionStorage.getItem(key)) || fallback } catch (e) { return fallback }
}
function write (key, value) {
  try { window.sessionStorage.setItem(key, JSON.stringify(value)) } catch (e) {}
}
const stored = read(queueKey, [])
let pending = (Array.isArray(stored) ? stored : []).filter(event => event && event.id && event.created > Date.now() - maxAge)
let sent = read(sentKey, {})
if (typeof sent !== 'object' || Array.isArray(sent)) sent = {}
Object.keys(sent).forEach(key => { if (sent[key] < Date.now() - maxAge) delete sent[key] })

function loadSdk () {
  if (window.fbq && typeof window.fbq.callMethod === 'function') return Promise.resolve()
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise((resolve, reject) => {
    if (!window.fbq) {
      const fbq = function () {
        if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments)
        else fbq.queue.push(arguments)
      }
      fbq.queue = []
      fbq.push = fbq
      fbq.loaded = true
      fbq.version = '2.0'
      window.fbq = window._fbq = fbq
    }
    const script = document.createElement('script')
    const finish = error => {
      clearTimeout(timer)
      script.onload = script.onerror = null
      if (error) {
        if (script.parentNode) script.parentNode.removeChild(script)
        reject(error)
      } else resolve()
    }
    const timer = setTimeout(() => finish(new Error('Meta Pixel SDK timed out')), 15000)
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    script.onload = () => finish(typeof window.fbq.callMethod === 'function' ? null : new Error('Meta Pixel SDK unavailable'))
    script.onerror = () => finish(new Error('Meta Pixel SDK failed to load'))
    document.head.appendChild(script)
  }).catch(error => { sdkPromise = null; throw error })
  return sdkPromise
}

function callPixel () {
  const fbq = window.fbq
  if (!fbq || typeof fbq.callMethod !== 'function') throw new Error('Meta Pixel SDK unavailable')
  const args = arguments
  const queue = fbq.queue
  const queued = Array.isArray(queue) ? queue.length : 0
  fbq.apply(window, args)
  // Some helpers wrap fbq before SDK load but leave the original stub disconnected
  // from callMethod. The helper records the call, then strands it in the old queue.
  // Forward only this newly queued command; remove it first to prevent a later replay.
  if (Array.isArray(queue) && queue.length === queued + 1) {
    const command = queue[queued]
    if (command && command.length === args.length &&
      Array.prototype.every.call(command, (value, index) => value === args[index])) {
      queue.splice(queued, 1)
      fbq.callMethod.apply(fbq, args)
    }
  }
}

function settle (event, result) {
  const completed = waiters.filter(waiter => waiter.event === event)
  waiters = waiters.filter(waiter => waiter.event !== event)
  completed.forEach(waiter => waiter.resolve(result))
}

function flush (target = pixelId) {
  if (target === null) return false // Configuration has not arrived yet.
  if (!target) {
    pending.filter(event => !event.pixel).forEach(event => settle(event, false))
    pending = pending.filter(event => event.pixel)
    write(queueKey, pending)
    return false
  }
  // Capture attribution before any network wait or route change.
  window.__metaAttribution.restore()
  const failed = () => {
    pending.filter(event => event.pixel === target).forEach(event => settle(event, false))
    return false // Keep queued events for a later attempt / page reload.
  }
  const send = () => {
    try {
      if (!initialized[target]) {
        callPixel('init', target)
        callPixel('trackSingle', target, 'PageView')
        initialized[target] = true
      }
      const batch = pending.filter(event => event.pixel === target)
      for (const event of batch) {
        const key = target + ':' + event.id
        if (!sent[key]) {
          callPixel('trackSingle', target, event.name, event.params, { eventID: event.id })
          sent[key] = Date.now()
          write(sentKey, sent)
        }
        pending = pending.filter(item => item !== event)
        write(queueKey, pending)
        // This confirms handoff to the SDK, not receipt or attribution by Meta.
        settle(event, true)
      }
      return true
    } catch (error) {
      return failed()
    }
  }
  // A ready SDK receives commands in this call stack, without an async flush lock.
  // Only downloading the official SDK requires a network completion callback.
  if (window.fbq && typeof window.fbq.callMethod === 'function') return send()
  return loadSdk().then(send, failed)
}

// A new referral is being resolved; don't send registration to the previous Pixel.
export function prepareMetaPixel () {
  pixelId = null
}

export function configureMetaPixel (id) {
  pixelId = id ? String(id) : ''
  // Only the init response may bind events awaiting configuration. A delayed
  // deposit for an older Pixel must not claim these unconfigured events.
  if (pixelId) {
    pending.forEach(event => { if (!event.pixel) event.pixel = pixelId })
    write(queueKey, pending)
  }
  return flush()
}

export function trackMetaEvent (name, params = {}, eventId, targetPixelId) {
  window.__metaAttribution.restore()
  const target = targetPixelId == null ? pixelId : String(targetPixelId)
  const id = eventId || name + ':' + Date.now() + ':' + Math.random().toString(36).slice(2)
  if (target && sent[target + ':' + id]) return Promise.resolve(true)
  let event = pending.find(event => event.id === id && event.pixel === (target || null))
  if (!event) {
    let snapshot
    try { snapshot = JSON.parse(JSON.stringify(params)) } catch (error) { return Promise.resolve(false) }
    event = { id, name, params: snapshot, created: Date.now(), pixel: target || null }
    pending.push(event)
    write(queueKey, pending)
  }
  const result = new Promise(resolve => {
    waiters.push({ event, resolve })
  })
  flush(event.pixel || target)
  return result
}



// WEBPACK FOOTER //
// ./src/common/utils/metaPixel.js